import { FocusEvent, KeyboardEvent, memo, useCallback, useState } from 'react';

import { MultiStoreCenterDay } from '../../../../interface/multi-store';
import {
  getArrowTargetIndex,
  getTabStopId,
} from '../../../../utils/multi-store/roving';
import { MultiStoreFreeCard } from './multi-store-free-card';

interface MultiStoreFreeCardsProps {
  // id del titolo «Dove vuoi andare alle …?», che dà il nome al gruppo
  titleId: string;
  // centri liberi all'orario scelto, il corrente per primo
  centers: MultiStoreCenterDay[];
  hour: string;
  anyUserCenterId?: string;
  onSwitch: (center: MultiStoreCenterDay, hour: string) => void;
}

// Schede dei centri liberi come gruppo di radio con tabindex mobile (pattern WAI-ARIA): Tab
// entra nel gruppo sulla scheda scelta ed esce con un passo solo, le frecce spostano il focus
// fra le schede. La scelta resta a Invio, barra spaziatrice e tocco: scegliere una scheda
// cambia il centro dell'appuntamento e la porta in cima all'elenco, quindi con le frecce che
// scelgono si cambierebbe centro a ogni passo e l'ordine cambierebbe sotto il focus.
export const MultiStoreFreeCards = memo(function MultiStoreFreeCards({
  titleId,
  centers,
  hour,
  anyUserCenterId,
  onSwitch,
}: MultiStoreFreeCardsProps): JSX.Element {
  // scheda col focus dentro il gruppo; uscendo dal gruppo si torna alla scheda scelta
  const [focusedId, setFocusedId] = useState<string>();

  const tabStopId = getTabStopId(
    centers.map((center) => center.pointOfSale.id),
    centers.find((center) => center.isCurrent)?.pointOfSale.id,
    focusedId
  );

  const handleKeyDown = useCallback((event: KeyboardEvent<HTMLDivElement>) => {
    const radios = Array.from(
      event.currentTarget.querySelectorAll<HTMLElement>('[role="radio"]')
    );
    const target = getArrowTargetIndex(
      event.key,
      radios.findIndex((radio) => radio === event.target),
      radios.length
    );
    if (target === undefined) return;
    // le frecce non scorrono la pagina
    event.preventDefault();
    radios[target].focus();
  }, []);

  const handleBlur = useCallback((event: FocusEvent<HTMLDivElement>) => {
    const next = event.relatedTarget;
    if (next instanceof Node && event.currentTarget.contains(next)) return;
    setFocusedId(undefined);
  }, []);

  return (
    <div
      role="radiogroup"
      aria-labelledby={titleId}
      className="multi-store-cards"
      onKeyDown={handleKeyDown}
      onBlur={handleBlur}
    >
      {centers.map((center) => (
        <MultiStoreFreeCard
          key={center.pointOfSale.id}
          center={center}
          hour={hour}
          isAnyUser={
            center.isAnyUser || center.pointOfSale.id === anyUserCenterId
          }
          isTabStop={center.pointOfSale.id === tabStopId}
          onFocusCenter={setFocusedId}
          onSwitch={onSwitch}
        />
      ))}
    </div>
  );
});
